<template>
  <div
    class="course-mega-menu"
    @mouseenter="openOnHover"
    @mouseleave="scheduleMegaMenuClose"
  >
    <button
      type="button"
      class="header-enroll-btn mr-2 border-0 shadow-none course-mega-menu__trigger"
      aria-haspopup="true"
      :aria-expanded="isMegaMenuOpen ? 'true' : 'false'"
      @click="toggleMegaMenu"
    >
      <span>Courses</span>
      <svg
        class="course-mega-menu__trigger-icon"
        viewBox="0 0 24 24"
        aria-hidden="true"
        fill="none"
      >
        <circle
          cx="12"
          cy="12"
          r="8.5"
          stroke="currentColor"
          stroke-width="1.8"
        />
        <path
          d="M12 8V15"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        />
        <path
          d="M9.5 12.5L12 15L14.5 12.5"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <transition name="course-mega-menu-fade">
      <div
        v-if="isMegaMenuOpen"
        class="course-mega-menu__panel"
        :class="toneClass"
        @mouseenter="cancelMegaMenuClose"
        @mouseleave="scheduleMegaMenuClose"
      >
        <div class="course-mega-menu__categories">
          <button
            v-for="category in getCategories"
            :key="category.ProgramCategory.ProgramCategoryId"
            type="button"
            class="course-mega-menu__category"
            :class="{
              'course-mega-menu__category--active':
                category.ProgramCategory.ProgramCategoryId == activeCategoryId,
            }"
            @mouseenter="selectCategory(category)"
            @focus="selectCategory(category)"
            @click="onCategoryClick(category)"
          >
            <span class="course-mega-menu__category-icon">
              <img
                v-if="category.ProgramCategory.ProgramCategoryICon"
                :src="category.ProgramCategory.ProgramCategoryICon"
                :alt="category.ProgramCategory.Category"
              />
            </span>

            <span class="course-mega-menu__category-title">
              {{ category.ProgramCategory.Category }}
            </span>
          </button>
        </div>

        <div class="course-mega-menu__levels">
          <button
            v-if="activeCategoryRecord"
            type="button"
            class="course-mega-menu__view-all"
            @click="goToCategory(activeCategoryRecord)"
          >
            View all {{ activeCategoryRecord.ProgramCategory.Category }}
          </button>
          <div
            v-if="primaryRecords.length"
            class="course-mega-menu__primary-grid"
          >
            <button
              v-for="record in primaryRecords"
              :key="`${primaryNode.NodeKey}-${getNodeId(record, primaryNode.NodeKey)}`"
              type="button"
              class="course-mega-menu__primary-option"
              :class="{
                'course-mega-menu__primary-option--active':
                  getNodeId(record, primaryNode.NodeKey) == activePrimaryId,
                'course-mega-menu__primary-option--text-only':
                  !getNodeIcon(record, primaryNode.NodeKey),
              }"
              @click="onPrimaryClick(record)"
            >
              <span
                v-if="getNodeIcon(record, primaryNode.NodeKey)"
                class="course-mega-menu__option-icon"
              >
                <img
                  :src="getNodeIcon(record, primaryNode.NodeKey)"
                  :alt="getNodeTitle(record, primaryNode.NodeKey)"
                />
              </span>

              <span class="course-mega-menu__primary-label">
                {{ getNodeTitle(record, primaryNode.NodeKey) }}
              </span>
            </button>
          </div>

          <div
            v-if="secondaryRecords.length"
            class="course-mega-menu__secondary-grid"
          >
            <button
              v-for="record in secondaryRecords"
              :key="`${secondaryNode.NodeKey}-${getNodeId(record, secondaryNode.NodeKey)}`"
              type="button"
              class="course-mega-menu__secondary-option"
              @click="goToSecondary(record)"
            >
              <span
                v-if="getNodeIcon(record, secondaryNode.NodeKey)"
                class="course-mega-menu__secondary-icon"
              >
                <img
                  :src="getNodeIcon(record, secondaryNode.NodeKey)"
                  :alt="getNodeTitle(record, secondaryNode.NodeKey)"
                />
              </span>

              <span>
                {{ getNodeTitle(record, secondaryNode.NodeKey) }}
              </span>
            </button>
          </div>

          <div
            v-if="programCategories.length && !primaryRecords.length"
            class="course-mega-menu__empty"
          >
            No courses available.
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { appConstants } from "../common/constants";
import { programCategories as programCategoriesStoreKeys } from "../store/keys";
import cryptojs from "../plugins/crypto";

export default {
  name: "CourseMegaMenu",

  data() {
    return {
      isMegaMenuOpen: false,
      selectedCategoryId: null,
      selectedPrimaryId: null,
      closeTimer: null,
    };
  },

  async serverPrefetch() {
    return this.loadProgramCategories();
  },

  mounted() {
    this.loadProgramCategories();
    document.addEventListener("keydown", this.onKeyDown);
    document.addEventListener("click", this.onOutsideClick);
  },

  beforeDestroy() {
    this.clearCloseTimer();
    document.removeEventListener("keydown", this.onKeyDown);
    document.removeEventListener("click", this.onOutsideClick);
  },

  watch: {
    $route() {
      this.closeMegaMenu(true);
    },
  },

  computed: {
    ...mapGetters(
      programCategoriesStoreKeys.namespace,
      programCategoriesStoreKeys.getters,
    ),

    activeCategoryId() {
      if (this.selectedCategoryId != null) {
        return this.selectedCategoryId;
      }

      if (!this.getCategories || !this.getCategories.length) {
        return null;
      }

      return this.getCategories[0].ProgramCategory.ProgramCategoryId;
    },

    activeCategoryRecord() {
      if (this.activeCategoryId == null || !this.getCategories) {
        return null;
      }

      return (
        this.getCategories.find(
          (record) =>
            record.ProgramCategory &&
            record.ProgramCategory.ProgramCategoryId == this.activeCategoryId,
        ) || null
      );
    },

    categoryFlow() {
      if (
        !this.activeCategoryRecord ||
        !Array.isArray(this.activeCategoryRecord.ProgramCategoryFlow)
      ) {
        return [];
      }

      return this.activeCategoryRecord.ProgramCategoryFlow.slice().sort(
        (a, b) => Number(a.SortOrder || 0) - Number(b.SortOrder || 0),
      );
    },

    menuFlow() {
      return this.categoryFlow
        .filter(
          (node) =>
            node.NodeKey !== appConstants.enums.programCategoryFlowKey.course,
        )
        .slice(0, 2);
    },

    primaryNode() {
      return this.menuFlow[0] || null;
    },

    secondaryNode() {
      return this.menuFlow[1] || null;
    },

    primaryRecords() {
      if (!this.primaryNode || this.activeCategoryId == null) {
        return [];
      }

      return this.getNodeRecords(this.primaryNode.NodeKey, {
        categoryId: this.activeCategoryId,
        programId: null,
        boardId: null,
        groupId: null,
      });
    },

    activePrimaryRecord() {
      if (!this.primaryNode || !this.primaryRecords.length) {
        return null;
      }

      if (this.selectedPrimaryId != null) {
        const selected = this.primaryRecords.find(
          (record) =>
            this.getNodeId(record, this.primaryNode.NodeKey) ==
            this.selectedPrimaryId,
        );

        if (selected) {
          return selected;
        }
      }

      return this.primaryRecords[0];
    },

    activePrimaryId() {
      if (!this.activePrimaryRecord || !this.primaryNode) {
        return null;
      }

      return this.getNodeId(
        this.activePrimaryRecord,
        this.primaryNode.NodeKey,
      );
    },

    secondaryRecords() {
      if (
        !this.secondaryNode ||
        !this.primaryNode ||
        !this.activePrimaryRecord
      ) {
        return [];
      }

      const filters = this.applyRecordToFilters(
        {
          categoryId: this.activeCategoryId,
          programId: null,
          boardId: null,
          groupId: null,
        },
        this.activePrimaryRecord,
        this.primaryNode.NodeKey,
      );

      return this.getNodeRecords(this.secondaryNode.NodeKey, filters);
    },

    toneClass() {
      switch (Number(this.activeCategoryId)) {
        case 2:
          return "course-mega-menu--admission";
        case 1:
          return "course-mega-menu--coaching";
        case 5:
          return "course-mega-menu--foreign";
        case 6:
          return "course-mega-menu--professional";
        default:
          return "";
      }
    },
  },

  methods: {
    ...mapActions(
      programCategoriesStoreKeys.namespace,
      programCategoriesStoreKeys.actions,
    ),

    loadProgramCategories() {
      return this.get_ProgramCategories({
        config: {
          loader: {
            show: false,
          },
        },
      });
    },

    openOnHover() {
      if (window.matchMedia("(min-width: 1200px) and (hover: hover)").matches) {
        this.openMegaMenu();
      }
    },

    toggleMegaMenu() {
      if (this.isMegaMenuOpen) this.closeMegaMenu(true);
      else this.openMegaMenu();
    },

    onOutsideClick(event) {
      if (!this.$el.contains(event.target)) this.closeMegaMenu(true);
    },

    onCategoryClick(category) {
      if (window.matchMedia("(max-width: 1199px), (hover: none)").matches) {
        this.selectCategory(category);
      } else {
        this.goToCategory(category);
      }
    },

    onPrimaryClick(record) {
      this.selectPrimary(record);
      if (!this.secondaryRecords.length) this.goToPrimary(record);
    },

    openMegaMenu() {
      this.clearCloseTimer();
      this.isMegaMenuOpen = true;
    },

    closeMegaMenu(resetSelection) {
      this.clearCloseTimer();
      this.isMegaMenuOpen = false;

      if (resetSelection) {
        this.selectedCategoryId = null;
        this.selectedPrimaryId = null;
      }
    },

    scheduleMegaMenuClose() {
      if (window.matchMedia("(max-width: 1199px), (hover: none)").matches) return;
      this.clearCloseTimer();
      this.closeTimer = setTimeout(() => {
        this.closeMegaMenu(true);
      }, 180);
    },

    cancelMegaMenuClose() {
      this.clearCloseTimer();
    },

    clearCloseTimer() {
      if (!this.closeTimer) {
        return;
      }

      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    },

    onKeyDown(event) {
      if (event.key === "Escape") {
        this.closeMegaMenu(true);
      }
    },

    selectCategory(category) {
      const categoryId = category.ProgramCategory.ProgramCategoryId;

      if (this.activeCategoryId == categoryId) {
        return;
      }

      this.selectedCategoryId = categoryId;
      this.selectedPrimaryId = null;
    },

    selectPrimary(record) {
      if (!this.primaryNode) {
        return;
      }

      this.selectedPrimaryId = this.getNodeId(
        record,
        this.primaryNode.NodeKey,
      );
    },

    getNodeRecords(nodeKey, filters) {
      const filteredRecords = this.filteredRecords(
        filters.categoryId,
        filters.programId,
        filters.boardId,
        filters.groupId,
      );

      const courseRecords = this.filterByLearningType(filteredRecords);
      const ids = new Set();

      return courseRecords.filter((record) => {
        const id = this.getNodeId(record, nodeKey);

        if (id === null || id === undefined || ids.has(id)) {
          return false;
        }

        ids.add(id);
        return true;
      });
    },

    getNodeId(record, nodeKey) {
      switch (nodeKey) {
        case appConstants.enums.programCategoryFlowKey.program:
          return record.AcademicProgram
            ? record.AcademicProgram.AcademicProgramId
            : null;

        case appConstants.enums.programCategoryFlowKey.board:
          return record.Board ? record.Board.BoardId : null;

        case appConstants.enums.programCategoryFlowKey.group:
          return record.Group ? record.Group.CourseGroupId : null;

        default:
          return null;
      }
    },

    getNodeTitle(record, nodeKey) {
      switch (nodeKey) {
        case appConstants.enums.programCategoryFlowKey.program:
          return record.AcademicProgram
            ? record.AcademicProgram.AcademicProgramName
            : "";

        case appConstants.enums.programCategoryFlowKey.board:
          return record.Board ? record.Board.BoardName : "";

        case appConstants.enums.programCategoryFlowKey.group:
          return record.Group
            ? record.Group.CourseGroupDisplayName || record.Group.CourseGroupName
            : "";

        default:
          return "";
      }
    },

    getNodeIcon(record, nodeKey) {
      switch (nodeKey) {
        case appConstants.enums.programCategoryFlowKey.board:
          return record.Board ? record.Board.BoardIcon : "";

        case appConstants.enums.programCategoryFlowKey.group:
          return record.Group ? record.Group.CourseGroupIcon : "";

        default:
          return "";
      }
    },

    applyRecordToFilters(filters, record, nodeKey) {
      const nextFilters = Object.assign({}, filters);
      const id = this.getNodeId(record, nodeKey);

      switch (nodeKey) {
        case appConstants.enums.programCategoryFlowKey.program:
          nextFilters.programId = id;
          break;

        case appConstants.enums.programCategoryFlowKey.board:
          nextFilters.boardId = id;
          break;

        case appConstants.enums.programCategoryFlowKey.group:
          nextFilters.groupId = id;
          break;
      }

      return nextFilters;
    },

    getNextNode(nodeKey) {
      const currentIndex = this.categoryFlow.findIndex(
        (node) => node.NodeKey === nodeKey,
      );

      return currentIndex >= 0
        ? this.categoryFlow[currentIndex + 1] || null
        : null;
    },

    encryptedParam(value) {
      const actualValue =
        value === null || value === undefined ? this.defaultRouteValue : value;

      return cryptojs.UTF.encrypt(actualValue);
    },

    buildRouteParams(categoryId, filters) {
      return {
        categoryId: this.encryptedParam(categoryId),
        programId: this.encryptedParam(filters.programId),
        groupId: this.encryptedParam(filters.groupId),
        boardId: this.encryptedParam(filters.boardId),
      };
    },

    getFiltersThroughNode(record, currentNodeKey) {
      const filters = {
        programId: null,
        boardId: null,
        groupId: null,
      };

      const currentIndex = this.categoryFlow.findIndex(
        (node) => node.NodeKey === currentNodeKey,
      );

      if (currentIndex < 0) {
        return filters;
      }

      this.categoryFlow.slice(0, currentIndex + 1).forEach((node) => {
        const nextFilters = this.applyRecordToFilters(
          filters,
          record,
          node.NodeKey,
        );

        filters.programId = nextFilters.programId;
        filters.boardId = nextFilters.boardId;
        filters.groupId = nextFilters.groupId;
      });

      return filters;
    },

    navigateToNode(node, categoryId, filters) {
      if (!node) {
        return;
      }

      const routeName = this.getRouteName(node.NodeKey);

      if (!routeName) {
        return;
      }

      const params = this.buildRouteParams(categoryId, filters);

      this.closeMegaMenu(true);

      this.$router
        .push({
          name: routeName,
          params,
        })
        .catch(() => {});
    },

    goToCategory(category) {
      const categoryId = category.ProgramCategory.ProgramCategoryId;
      const categoryFlow = Array.isArray(category.ProgramCategoryFlow)
        ? category.ProgramCategoryFlow.slice().sort(
            (a, b) => Number(a.SortOrder || 0) - Number(b.SortOrder || 0),
          )
        : [];
      const firstNode = categoryFlow[0];

      if (!firstNode) {
        return;
      }

      this.closeMegaMenu(true);

      this.$router
        .push({
          name: this.getRouteName(firstNode.NodeKey),
          params: {
            categoryId: cryptojs.UTF.encrypt(categoryId),
          },
        })
        .catch(() => {});
    },

    goToPrimary(record) {
      if (!this.primaryNode) {
        return;
      }

      const categoryId =
        record.ProgramCategory && record.ProgramCategory.ProgramCategoryId != null
          ? record.ProgramCategory.ProgramCategoryId
          : this.activeCategoryId;

      const filters = this.getFiltersThroughNode(
        record,
        this.primaryNode.NodeKey,
      );

      this.navigateToNode(
        this.getNextNode(this.primaryNode.NodeKey),
        categoryId,
        filters,
      );
    },

    goToSecondary(record) {
      if (!this.primaryNode || !this.secondaryNode) {
        return;
      }

      const categoryId =
        record.ProgramCategory && record.ProgramCategory.ProgramCategoryId != null
          ? record.ProgramCategory.ProgramCategoryId
          : this.activeCategoryId;

      const filters = this.getFiltersThroughNode(
        record,
        this.secondaryNode.NodeKey,
      );

      this.navigateToNode(
        this.getNextNode(this.secondaryNode.NodeKey),
        categoryId,
        filters,
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.course-mega-menu {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
}

.course-mega-menu__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
}

.course-mega-menu__trigger-icon {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
}

.course-mega-menu__panel {
  position: fixed;
  top: 76px;
  left: 50%;
  z-index: 1001;

  display: grid;
  grid-template-columns: 290px minmax(0, 1fr);

  width: calc(100vw - 64px);
  max-width: 1240px;
  min-height: 370px;

  transform: translateX(-50%);

  border: 1.5px solid #5d8df4;
  border-radius: 18px;
  background: #f7f4f4;
  box-shadow: 0 18px 50px rgba(23, 25, 59, 0.16);
  overflow: hidden;
}

.course-mega-menu__categories {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 30px 20px 28px 26px;
  border-right: 1px solid #dedada;
}

.course-mega-menu__category {
  position: relative;
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr);
  align-items: center;
  gap: 14px;

  width: 100%;
  min-height: 82px;
  margin: 0;
  padding: 10px 12px;

  border: 0;
  border-bottom: 1px solid #ddd8d8;
  background: transparent;

  color: #161616;
  text-align: left;
  font-family: "Poppins-Regular";
  font-size: 18px;
  line-height: 1.08;
  cursor: pointer;

  transition: background-color 0.15s ease;
}

.course-mega-menu__category:last-child {
  border-bottom: 0;
}

.course-mega-menu__category:hover,
.course-mega-menu__category:focus,
.course-mega-menu__category--active {
  background: rgba(98, 101, 183, 0.07);
  outline: none;
}

.course-mega-menu__category--active::before {
  content: "";
  position: absolute;
  top: 12px;
  bottom: 12px;
  left: 0;
  width: 3px;
  border-radius: 4px;
  background: #6265b7;
}

.course-mega-menu__category-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
}

.course-mega-menu__category-icon img {
  display: block;
  width: 58px;
  height: 58px;
  object-fit: contain;
}

.course-mega-menu__category-title {
  display: block;
  overflow-wrap: anywhere;
}

.course-mega-menu__levels {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 40px 34px 34px 40px;
}

.course-mega-menu__primary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(135px, 1fr));
  gap: 20px;
  width: 100%;
  max-width: 850px;
}

.course-mega-menu__primary-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;

  min-width: 0;
  min-height: 120px;
  margin: 0;
  padding: 10px;

  border: 2px solid transparent;
  border-radius: 18px;
  background: #ccebdd;

  color: #111;
  font-family: "Poppins-Regular";
  cursor: pointer;

  transition:
    border-color 0.15s ease,
    transform 0.15s ease,
    box-shadow 0.15s ease;
}

.course-mega-menu__primary-option:hover,
.course-mega-menu__primary-option:focus,
.course-mega-menu__primary-option--active {
  border-color: rgba(98, 101, 183, 0.55);
  outline: none;
  box-shadow: 0 5px 15px rgba(49, 50, 99, 0.1);
  transform: translateY(-1px);
}

.course-mega-menu__option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
}

.course-mega-menu__option-icon img {
  display: block;
  width: 56px;
  height: 56px;
  object-fit: contain;
}

.course-mega-menu__primary-label {
  display: block;
  max-width: 100%;
  font-size: 21px;
  line-height: 1.05;
  text-align: center;
  overflow-wrap: anywhere;
}

.course-mega-menu__primary-option--text-only
  .course-mega-menu__primary-label {
  font-size: 30px;
}

.course-mega-menu__secondary-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(115px, 1fr));
  gap: 18px;

  width: 100%;
  max-width: 900px;
  margin-top: 28px;
}

.course-mega-menu__secondary-option {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  min-width: 0;
  min-height: 56px;
  margin: 0;
  padding: 8px 10px;

  border: 1px solid transparent;
  border-radius: 17px;
  background: #fff;

  color: #111;
  font-family: "Poppins-Regular";
  font-size: 18px;
  line-height: 1.05;
  text-align: center;
  cursor: pointer;

  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;
}

.course-mega-menu__secondary-option:hover,
.course-mega-menu__secondary-option:focus {
  border-color: rgba(98, 101, 183, 0.35);
  background: #fafafa;
  outline: none;
}

.course-mega-menu__secondary-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
}

.course-mega-menu__secondary-icon img {
  display: block;
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.course-mega-menu__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  color: #62626b;
  font-size: 16px;
}

.course-mega-menu--admission .course-mega-menu__primary-option {
  background: #ccebdd;
}

.course-mega-menu--coaching .course-mega-menu__primary-option {
  background: #dfe1f5;
}

.course-mega-menu--foreign .course-mega-menu__primary-option {
  background: #ffed97;
}

.course-mega-menu--professional .course-mega-menu__primary-option {
  background: #d9e5ff;
}

.course-mega-menu-fade-enter-active,
.course-mega-menu-fade-leave-active {
  transition: opacity 0.12s ease;
}

.course-mega-menu-fade-enter,
.course-mega-menu-fade-leave-to {
  opacity: 0;
}

@media (min-width: 1200px) and (max-width: 1550px) {
  .course-mega-menu__panel {
    top: 72px;
    grid-template-columns: 255px minmax(0, 1fr);
    width: calc(100vw - 50px);
    max-width: 1120px;
    min-height: 345px;
  }

  .course-mega-menu__categories {
    padding: 25px 17px 24px 22px;
  }

  .course-mega-menu__category {
    grid-template-columns: 50px minmax(0, 1fr);
    gap: 11px;
    min-height: 72px;
    padding: 8px 10px;
    font-size: 15px;
  }

  .course-mega-menu__category-icon,
  .course-mega-menu__category-icon img {
    width: 50px;
    height: 50px;
  }

  .course-mega-menu__levels {
    padding: 30px 28px;
  }

  .course-mega-menu__primary-grid {
    gap: 20px;
  }

  .course-mega-menu__primary-option {
    min-height: 110px;
    border-radius: 15px;
  }

  .course-mega-menu__option-icon {
    width: 52px;
    height: 52px;
  }

  .course-mega-menu__option-icon img {
    width: 48px;
    height: 48px;
  }

  .course-mega-menu__primary-label {
    font-size: 18px;
  }

  .course-mega-menu__primary-option--text-only
    .course-mega-menu__primary-label {
    font-size: 30px;
  }

  .course-mega-menu__secondary-grid {
    gap: 14px;
    margin-top: 24px;
  }

  .course-mega-menu__secondary-option {
    min-height: 60px;
    font-size: 15px;
  }
}

.course-mega-menu__view-all {
  display: none;
}

@media (max-width: 1199px) {
  .course-mega-menu__panel {
    top: 80px;
    width: calc(100vw - 24px);
    max-height: calc(100vh - 96px);
    max-height: calc(100dvh - 96px);
    min-height: 0;
    overflow-y: auto;
    grid-template-columns: minmax(0, 1fr);
    border-radius: 12px;
  }

  .course-mega-menu__categories {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
    padding: 10px;
    border-right: 0;
    border-bottom: 1px solid #e8e4e4;
  }

  .course-mega-menu__category {
    grid-template-columns: 24px minmax(0, 1fr);
    gap: 8px;
    min-height: 52px;
    padding: 8px;
    border-bottom: 0;
    font-size: 12px;
    line-height: 1.2;
  }

  .course-mega-menu__category--active::before { display: none; }
  .course-mega-menu__category-icon,
  .course-mega-menu__category-icon img { width: 24px; height: 24px; }

  .course-mega-menu__levels { padding: 12px; }
  .course-mega-menu__view-all {
    display: block;
    align-self: flex-start;
    margin: 0 0 12px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #6163b0;
    font: inherit;
    font-size: 14px;
    text-align: left;
    cursor: pointer;
  }

  .course-mega-menu__primary-grid,
  .course-mega-menu__secondary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .course-mega-menu__primary-option {
    min-height: 88px;
    padding: 10px 8px;
    gap: 6px;
    border-radius: 14px;
  }
  .course-mega-menu__option-icon,
  .course-mega-menu__option-icon img { width: 36px; height: 36px; }
  .course-mega-menu__primary-label { font-size: 14px; line-height: 1.2; }
  .course-mega-menu__primary-option--text-only .course-mega-menu__primary-label {
    font-size: 20px;
  }
  .course-mega-menu__secondary-grid { margin-top: 14px; }
  .course-mega-menu__secondary-option {
    min-height: 54px;
    padding: 8px;
    border-radius: 12px;
    font-size: 12px;
  }
}
</style>
